/**
 * The Supabase URL a BROWSER talks to, resolved at runtime.
 *
 * `process.env.NEXT_PUBLIC_*` is inlined when the image is built, and one image
 * runs in every environment, so the build-time value is the LEAST reliable
 * source. The browser client (`./client`) and anything that must agree with it
 * about which Supabase host is "ours" (e.g. `lib/personaAvatar.ts`) resolve it
 * here, in one order:
 *   1. deriveSupabaseUrlFromHostname() - self-hosted: derived from the page host
 *   2. window.__RUNTIME_CONFIG__.NEXT_PUBLIC_SUPABASE_URL - injected per request
 *      by components/RuntimeConfig.tsx (from SUPABASE_PUBLIC_URL)
 *   3. process.env.NEXT_PUBLIC_SUPABASE_URL - build-time fallback
 *
 * Dependency-free on purpose: importing `./client` would pull in @supabase/ssr.
 */

/**
 * Derive Supabase URL dynamically from current hostname.
 * This ensures multi-tenant and domain-change scenarios work without cache issues.
 *
 * Pattern: https://api-supabase.{env}.neutralaiz.com
 * Examples:
 *   - staging.neutralaiz.com -> https://api-supabase.staging.neutralaiz.com (self-hosted)
 *   - demo.denvr.neutralaiz.com -> https://api-supabase.denvr.neutralaiz.com (self-hosted)
 *   - test.neutralaiz.com -> uses env var (Supabase Cloud)
 *   - neutralaiz.com -> uses env var (Supabase Cloud)
 *   - demo.neutralaiz.com -> https://api-supabase.demo.neutralaiz.com (self-hosted)
 *
 * NOTE: Production (neutralaiz.com) uses Supabase Cloud.
 * Cloud domains return null to let the env var take precedence.
 */
export function deriveSupabaseUrlFromHostname(): string | null {
    if (typeof window === 'undefined') return null;

    const hostname = window.location.hostname;

    // Must be a neutralaiz.com domain
    if (!hostname.endsWith('.neutralaiz.com') && hostname !== 'neutralaiz.com') {
        return null;
    }

    // Production/Cloud domains use Supabase Cloud - don't derive, use env var instead
    // These domains should use build-time NEXT_PUBLIC_SUPABASE_URL (Supabase Cloud)
    // NOTE: demo.neutralaiz.com removed — demo uses self-hosted Supabase (supabase-selfhosted K8s component)
    const cloudDomains = [
        'neutralaiz.com',
        'portal.neutralaiz.com',
    ];
    if (cloudDomains.includes(hostname)) {
        return null;  // Let NEXT_PUBLIC_SUPABASE_URL take precedence (Supabase Cloud)
    }

    // Extract environment from hostname
    // Pattern: [subdomain].[env].neutralaiz.com
    const parts = hostname.split('.');
    const neutralaizIndex = parts.indexOf('neutralaiz');

    if (neutralaizIndex < 1) return null;

    // Known self-hosted environments (these have their own Supabase instances)
    // Known self-hosted environment identifiers only. Retired environments are
    // deliberately absent so an old hostname cannot silently regain routing.
    const selfHostedEnvs = ['denvr', 'dev', 'staging', 'test', 'demo'];

    // Walk backwards from 'neutralaiz' to find the environment
    for (let i = neutralaizIndex - 1; i >= 0; i--) {
        if (selfHostedEnvs.includes(parts[i])) {
            // The self-hosted Supabase host is ALWAYS `api-supabase.<app-host>` in the
            // domain model — NOT `api-supabase.<env>.neutralaiz.com`. Using the env label
            // + a hardcoded `.neutralaiz.com` suffix breaks any env that lives under an
            // intermediate apex, e.g. staging.engageiq.neutralaiz.com would wrongly derive
            // api-supabase.staging.neutralaiz.com (unroutable). Prepend to the FULL hostname
            // so both bare (demo.neutralaiz.com) and nested (staging.engageiq.neutralaiz.com)
            // families resolve to their own api-supabase.<host>.
            return `https://api-supabase.${hostname}`;
        }
    }

    // No self-hosted env found - let env var take precedence
    // This handles cases like unknown subdomains that might use Supabase Cloud
    return null;
}

export type BrowserSupabaseUrlSource =
    | 'deriveSupabaseUrlFromHostname'
    | 'RuntimeConfig'
    | 'NEXT_PUBLIC_SUPABASE_URL';

/**
 * Resolve the browser Supabase URL in the order above. On the server (no
 * `window`) only the build-time value can apply. `url` is undefined when no
 * source resolves.
 */
export function resolveBrowserSupabaseUrl(): {
    url: string | undefined;
    source: BrowserSupabaseUrlSource;
} {
    const derivedUrl = deriveSupabaseUrlFromHostname();
    const runtimeUrl =
        typeof window === 'undefined' ? undefined : window.__RUNTIME_CONFIG__?.NEXT_PUBLIC_SUPABASE_URL;
    const envUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const url = derivedUrl || runtimeUrl || envUrl || undefined;
    const source: BrowserSupabaseUrlSource = derivedUrl
        ? 'deriveSupabaseUrlFromHostname'
        : runtimeUrl
            ? 'RuntimeConfig'
            : 'NEXT_PUBLIC_SUPABASE_URL';
    return { url, source };
}
